<template>
  <div class="relative cursor-pointer">
    <div
      class="w-9 h-9 flex items-center justify-center rounded-full bg-gray-50 text-primary hover:bg-gray-100 transition-colors"
      @click="toggleNotificationsPanel">
      <i class="fas fa-bell text-sm"></i>
      <span v-if="notificationCount > 0"
        class="absolute -top-1 -right-1 w-[18px] h-[18px] flex items-center justify-center rounded-full bg-green-500 text-white text-[10px] font-bold border-2 border-white">
        {{ notificationCount > 9 ? '9+' : notificationCount }}
      </span>
    </div>

    <!-- Panneau -->
    <div v-if="showNotificationsPanel"
      class="absolute top-12 right-[-100px] w-80 bg-white rounded-xl border border-gray-200 shadow-lg z-[1000] max-h-[450px] flex flex-col overflow-hidden">
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <h3 class="text-sm font-semibold text-gray-900">Notifications</h3>
        <button v-if="notifications.length > 0"
          class="text-[11px] font-medium text-primary hover:underline transition-colors"
          @click="markAllNotificationsAsRead">
          Tout marquer comme lu
        </button>
      </div>

      <div class="flex-1 overflow-y-auto max-h-[350px]">
        <div v-if="notificationsLoading" class="py-8 text-center text-sm text-gray-400">
          <i class="fas fa-spinner fa-spin text-xs mr-1"></i>
          Chargement...
        </div>

        <div v-else-if="notifications.length === 0" class="py-8 text-center">
          <div class="text-gray-300 mb-2">
            <i class="fas fa-bell-slash text-xl"></i>
          </div>
          <p class="text-xs text-gray-400">Aucune notification non lue</p>
        </div>

        <div v-else>
          <NotificationItem v-for="notification in notifications" :key="notification.id"
            :notification="notification" @mark-as-read="markNotificationAsRead"
            @click="handleNotificationClick" />
        </div>
      </div>

      <div class="px-4 py-3 border-t border-gray-100">
        <button
          class="w-full py-1.5 text-xs font-medium text-gray-500 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
          @click="viewAllNotifications">
          Voir toutes les notifications
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { notificationsService } from '@/services';
import NotificationItem from '@/views/notifications/NotificationItem.vue';
import { useNotificationsStore } from '@/store/index';
import { storeToRefs } from 'pinia';

export default {
  name: 'NotificationPanel',
  components: {
    NotificationItem
  },
  setup() {
    const notificationsStore = useNotificationsStore();
    const { notificationCount } = storeToRefs(notificationsStore);

    return {
      notificationsStore,
      notificationCount
    };
  },
  data() {
    return {
      showNotificationsPanel: false,
      notifications: [],
      notificationsLoading: false,
      notificationsPage: 1,
      notificationsPerPage: 5
    };
  },
  created() {
    this.notificationsStore.loadNotificationCount();
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    async loadNotifications() {
      if (this.notificationsLoading) return;

      this.notificationsLoading = true;
      try {
        const response = await notificationsService.getUserNotifications({
          page: this.notificationsPage,
          perPage: this.notificationsPerPage,
          unread: true
        });

        if (response.data.data) {
          this.notifications = response.data.data;
        } else {
          this.notifications = response.data;
        }
      } catch (error) {
        console.error('Erreur lors du chargement des notifications:', error);
      } finally {
        this.notificationsLoading = false;
      }
    },

    toggleNotificationsPanel() {
      this.showNotificationsPanel = !this.showNotificationsPanel;

      if (this.showNotificationsPanel) {
        this.loadNotifications();
      }
    },

    async markNotificationAsRead(notification) {
      try {
        await notificationsService.markAsRead(notification.id);

        const index = this.notifications.findIndex(n => n.id === notification.id);
        if (index !== -1) {
          this.notifications[index].read_at = new Date().toISOString();
        }

        const newCount = Math.max(0, this.notificationCount - 1);
        this.notificationsStore.updateNotificationCount(newCount);

      } catch (error) {
        console.error('Erreur lors du marquage de la notification comme lue:', error);
      }
    },

    async markAllNotificationsAsRead() {
      try {
        await notificationsService.markAllAsRead();

        this.notifications.forEach(notification => {
          notification.read_at = new Date().toISOString();
        });

        this.notificationsStore.updateNotificationCount(0);

      } catch (error) {
        console.error('Erreur lors du marquage de toutes les notifications comme lues:', error);
      }
    },

    handleNotificationClick(notification) {
      this.showNotificationsPanel = false;

      switch (notification.type) {
        case 'new_sale':
          if (notification.data && notification.data.sale_id) {
            this.$router.push({ name: 'sale-details', params: { id: notification.data.sale_id } });
          } else {
            this.$router.push({ name: 'sales' });
          }
          break;
        case 'low_stock':
          this.$router.push({ name: 'products' });
          break;
        case 'payment_received':
          this.$router.push({ name: 'sales' });
          break;
        case 'refund':
          if (notification.data && notification.data.sale_id) {
            this.$router.push({ name: 'sale-details', params: { id: notification.data.sale_id } });
          } else {
            this.$router.push({ name: 'sales' });
          }
          break;
        case 'new_customer':
          this.$router.push({ name: 'customers' });
          break;
        default:
          break;
      }
    },

    viewAllNotifications() {
      this.showNotificationsPanel = false;
      this.$router.push({ name: 'notifications' });
    },

    handleClickOutside(event) {
      const panel = this.$el;
      if (panel && !panel.contains(event.target) && this.showNotificationsPanel) {
        this.showNotificationsPanel = false;
      }
    }
  }
};
</script>
